import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import {
    Tooltip,
    IconButton,
} from '@material-ui/core';
import {
    PublishOutlined as UploadIcon,
    DeleteOutline as DeleteIcon,
} from '@material-ui/icons';

import { Typography } from '../../components/Wrappers/Wrappers.jsx';
import { DEFAULT_IMAGE } from '../../commons/imageUploader/DefaultImage';
import useStyles from './PhotoUpload.styles';

function readFileAsDataURL (file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
    });
}

function getImageSource (imageSource) {
    return imageSource || DEFAULT_IMAGE;
}

function getAltImageSource (imageSource) {
    return imageSource ? 'ECAL World Cover Photo' : 'No photo uploaded';
}

function getClassName (imageSource, classes) {
    return imageSource ? classes.image : classes.imagePlaceholder;
}

function PhotoUpload (props) {
    const {
        title,
        description,
        imageSource,
        onUpload,
        onDelete,
        width,
        height,
    } = props;

    const classes = useStyles({ width, height });
    const fileInputRef = useRef(null);

    async function handleFileChange (e) {
        const file = e.target.files[0];
        e.target.value = '';
        if (!file) {
            return;
        }
        try {
            const dataUrl = await readFileAsDataURL(file);
            onUpload(dataUrl, file);
        } catch (error) {
            console.error('PhotoUpload.handleFileChange() -> readFileAsDataURL failed', error);
        }
    }

    return (
        <div>
            { (title || description) && (
                <div className={classes.imageTitle}>
                    { title && (
                        <Typography
                            variant={'h6'}
                            style={{ marginBottom: 8 }}
                        >
                            { title }
                        </Typography>
                    )}
                    { description && (
                        <Typography variant={'body1'}>
                            { description }
                        </Typography>
                    )}
                </div>
            )}

            <div className={classes.logoDisplay}>
                <div className={classes.imageBox}>
                    <img
                        src={getImageSource(imageSource)}
                        alt={getAltImageSource(imageSource)}
                        className={getClassName(imageSource, classes)}
                    />
                </div>

                <div className={classes.imageControls}>
                    <Tooltip title={'Upload Image'}>
                        <IconButton
                            className={classes.iconButton}
                            color={'primary'}
                            onClick={() => fileInputRef.current.click()}
                        >
                            <UploadIcon fontSize={'medium'} />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title={'Delete Image'}>
                        {/*
                          * span wrapper so the tooltip still works while the button is disabled.
                          * color="primary" only shows while enabled — MUI overrides it to the
                          * standard disabled gray whenever disabled is true, which is intentional.
                          */}
                        <span>
                            <IconButton
                                className={classes.iconButton}
                                color={'primary'}
                                disabled={!imageSource}
                                onClick={onDelete}
                            >
                                <DeleteIcon fontSize={'medium'} />
                            </IconButton>
                        </span>
                    </Tooltip>
                </div>

                <input
                    ref={fileInputRef}
                    type={'file'}
                    accept={'image/*'}
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                />
            </div>
        </div>
    );
}

PhotoUpload.propTypes = {
    title: PropTypes.string,
    description: PropTypes.string,
    imageSource: PropTypes.string,
    onUpload: PropTypes.func,
    onDelete: PropTypes.func,
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

PhotoUpload.defaultProps = {
    title: '',
    description: '',
    imageSource: '',
    onUpload: () => {},
    onDelete: () => {},
    width: 388,
    height: 288,
};

export default PhotoUpload;
